import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Hash } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { EditMasterDialog } from "@/components/masters/EditMasterDialog";
import { RESET_FREQUENCIES } from "@/lib/roles";
import { CompanyField } from "@/components/CompanyField";

const EMPTY = { doc_type: "", prefix: "", separator: "/", padding: 4, reset_frequency: "YEARLY", fiscal_year_start_month: 4, company_id: "" };

export default function NumberingSeries() {
  const { can } = usePermissions();
  const canCreateSeries = can("NUMBERING_SERIES", "create");
  const canEditSeries = can("NUMBERING_SERIES", "edit");
  const { companies } = useCompany() || {};
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/numbering-series");
      setSeries(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "Organisation-wide";

  const preview = (s) => {
    const parts = [s.prefix];
    if (s.current_period_key) parts.push(s.current_period_key);
    parts.push(String(s.next_number).padStart(s.padding, "0"));
    return parts.join(s.separator);
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/numbering-series/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const issue = async (s) => {
    setIssuing(s.id);
    try {
      const { data } = await api.post(`/numbering-series/${s.id}/issue`);
      toast.success(`Issued: ${data.document_number}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIssuing(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="numbering-series-page">
      <PageHeader
        title="Numbering Series"
        description="Concurrent-safe document numbering with yearly, monthly or no reset."
        action={canCreateSeries && <CreateSeriesDialog companies={companies} onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && series.length === 0 ? (
          <EmptyState title="No numbering series yet" description="Create one to start issuing document numbers like PO/FY2627/0001." testId="numbering-series-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Doc type</TableHead><TableHead>Scope</TableHead><TableHead>Reset</TableHead>
                <TableHead>Next number</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {series.map((s) => (
                <TableRow key={s.id} data-testid={`numbering-series-row-${s.doc_type}`}>
                  <TableCell className="font-mono text-xs text-foreground">{s.doc_type}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(s.company_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{s.reset_frequency}</TableCell>
                  <TableCell className="font-mono text-xs font-semibold text-foreground">{preview(s)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={s.is_active} disabled={!canEditSeries} onCheckedChange={() => toggleActive(s)} data-testid={`numbering-series-active-toggle-${s.doc_type}`} />
                      <Badge variant={s.is_active ? "default" : "secondary"}>{s.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEditSeries && (
                        <EditMasterDialog
                          title={`Edit numbering series — ${s.doc_type}`} testId={`edit-numbering-series-${s.doc_type}`} initial={s}
                          fields={[
                            { key: "prefix", label: "Prefix" },
                            { key: "separator", label: "Separator" },
                            { key: "padding", label: "Digits (zero-padded)", type: "number", min: 1, max: 10 },
                            { key: "reset_frequency", label: "Restart numbering", type: "select", options: RESET_FREQUENCIES.map((r) => ({ value: r, label: r })) },
                            { key: "fiscal_year_start_month", label: "Financial year starts in month (1-12)", type: "number", min: 1, max: 12 },
                          ]}
                          note="Changes apply to numbers issued from now on. Numbers already issued are not renamed."
                          onSave={(v) => api.patch(`/numbering-series/${s.id}`, v)} onSaved={load}
                        />
                      )}
                      <Button size="sm" variant="outline" disabled={!s.is_active || issuing === s.id} onClick={() => issue(s)} data-testid={`issue-number-button-${s.doc_type}`}>
                      {issuing === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hash className="h-3.5 w-3.5" />} Issue test number
                    </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateSeriesDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/numbering-series", {
        ...form,
        padding: Number(form.padding),
        fiscal_year_start_month: Number(form.fiscal_year_start_month),
        company_id: form.company_id || null,
      });
      toast.success(`Numbering series ${form.doc_type} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-numbering-series-button"><Plus className="h-4 w-4" /> New Series</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Numbering Series</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-numbering-series-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Doc type</Label>
              <Input required data-testid="numbering-doc-type-input" value={form.doc_type} onChange={set("doc_type")} className="mt-1.5" placeholder="PO" />
              <FieldError error={errors.doc_type} />
            </div>
            <div>
              <Label>Prefix</Label>
              <Input required data-testid="numbering-prefix-input" value={form.prefix} onChange={set("prefix")} className="mt-1.5" placeholder="PO" />
              <FieldError error={errors.prefix} />
            </div>
          </div>
          <CompanyField label="Company (optional — leave unset for org-wide)" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="numbering-company-select" required={false} placeholder="Organisation-wide" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Reset</Label>
              <Select value={form.reset_frequency} onValueChange={(v) => setForm({ ...form, reset_frequency: v })}>
                <SelectTrigger data-testid="numbering-reset-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{RESET_FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Padding</Label>
              <Input type="number" min="1" max="10" data-testid="numbering-padding-input" value={form.padding} onChange={set("padding")} className="mt-1.5" />
            </div>
            <div>
              <Label>Separator</Label>
              <Input data-testid="numbering-separator-input" value={form.separator} onChange={set("separator")} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-numbering-series-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
